import React from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth } from 'date-fns';
import MonthlyMealTile from './MonthlyMealTile';
import { MealPlan } from '@/types/mealPlan';
import { MealNote } from '@/types/mealNote';
import { Recipe } from '@/types/recipe';
import { MealCompositionWithRecipes } from '@/types/mealComposition';

interface MonthlyCalendarGridProps {
  currentDate: Date;
  mealPlans: MealPlan[];
  mealNotes: MealNote[];
  recipes: Recipe[];
  compositions: MealCompositionWithRecipes[];
  isLoading: boolean;
}

const MonthlyCalendarGrid: React.FC<MonthlyCalendarGridProps> = ({
  currentDate,
  mealPlans,
  mealNotes,
  recipes,
  compositions,
  isLoading,
}) => {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  
  const calendarDays = eachDayOfInterval({
    start: calendarStart,
    end: calendarEnd,
  });

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Group meal plans and notes by date
  const mealPlansByDate = mealPlans.reduce((acc, plan) => {
    if (!acc[plan.date]) acc[plan.date] = [];
    acc[plan.date].push(plan);
    return acc;
  }, {} as Record<string, MealPlan[]>);

  const mealNotesByDate = mealNotes.reduce((acc, note) => {
    if (!acc[note.date]) acc[note.date] = [];
    acc[note.date].push(note);
    return acc;
  }, {} as Record<string, MealNote[]>);

  return (
    <div className="w-full">
      {/* Calendar grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
        {calendarDays.map((day) => {
          const dateString = format(day, 'yyyy-MM-dd');
          const dayMealPlans = mealPlansByDate[dateString] || [];
          const dayMealNotes = mealNotesByDate[dateString] || [];
          const isCurrentMonth = isSameMonth(day, currentDate);
          
          return (
            <MonthlyMealTile
              key={dateString}
              date={day}
              mealPlans={dayMealPlans}
              mealNotes={dayMealNotes}
              recipes={recipes}
              compositions={compositions}
              isLoading={isLoading}
              isCurrentMonth={isCurrentMonth}
            />
          );
        })}
      </div>
    </div>
  );
};

export default MonthlyCalendarGrid;